import React, { useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  map,
  pipe,
  propEq,
  slice,
  filter,
  mergeRight,
  prop,
  equals,
  reduce,
  length,
  add,
  find,
  head,
} from "ramda";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { isBlank, notBlank } from "../../util/ext";
import Tooltip from "@mui/material/Tooltip";
import { Link, useResolvedPath } from "react-router-dom";
import ContentFacetMenu from "../../creator-application/content/browse/ContentFacetMenu";
import Grid from "@mui/material/Grid";
import CustomTablePagination from "../../creator-application/common/CustomTablePagination";

const DEFAULT_ROWS_PER_PAGE = 20;

const PROCESSING_STATUS_LABELS = {
  SUCCESS: "Success",
  FAILED: "Failed",
};

export const processingStatuses = ["SUCCESS", "FAILED"];

export const getProcessingStatusLabel = status =>
  PROCESSING_STATUS_LABELS[status];

export const processingStatusTerms = {
  terms: map(
    status => ({
      label: getProcessingStatusLabel(status),
      queryParam: status,
    }),
    processingStatuses,
  ),
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 650px)",
    overflow: "auto",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const JobChunksTable = props => {
  const { chunks, page, onPageChange, onScrollVisible } = props;

  const availableFilters = [
    {
      name: "status",
      displayName: "Status",
      terms: processingStatusTerms.terms,
    },
  ];

  const classes = useStyles();
  const url = useResolvedPath(".").pathname;
  const [statusFilter, setStatusFilter] = useState(null);
  const [filteredChunks, setFilteredChunks] = useState(chunks);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  useEffect(() => {
    if (notBlank(statusFilter)) {
      const statuses = [
        {
          label: getProcessingStatusLabel(statusFilter),
          queryParam: statusFilter,
        },
      ];

      console.log("Statuses");
      console.log(statuses);

      setSelectedFilters([
        { name: "status", displayName: "Status", selectedTerms: statuses },
      ]);
    } else {
      setSelectedFilters([]);
    }
  }, [statusFilter]);

  const handleFilterSelected = filter => {
    console.log("filter", filter);
    const terms = filter.terms;
    setStatusFilter(head(terms)?.queryParam);
  };

  const handleClearAllFilters = () => {
    console.log("clear all filters");
    setStatusFilter(null);
  };

  //-- Manage nested menu items
  const [selectedFilter, setSelectedFilter] = useState("");
  const handleFilterClick = filterName => {
    equals(selectedFilter, filterName)
      ? setSelectedFilter("")
      : setSelectedFilter(filterName);
  };

  useEffect(() => {
    if (isBlank(statusFilter)) {
      setFilteredChunks(chunks);
      onPageChange(null, 0);
    } else {
      console.log("statusFilter", statusFilter);
      const chunksPage = pipe(filter(propEq("status", statusFilter)))(chunks);
      setFilteredChunks(chunksPage);
      onPageChange(null, 0);
    }
  }, [chunks, onPageChange, statusFilter]);

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Grid container xs={12} justifyContent="flex-end" alignItems="center">
          <Grid item xs="auto">
            <ContentFacetMenu
              useCustomStatusFacet={false}
              facets={map(filter => {
                const filterType = find(
                  propEq("name", filter.name),
                  selectedFilters,
                );
                // selected & selectedTerms tracks total to display for each autocomplete
                console.log("filterType", filterType);
                return mergeRight(filter, {
                  selected: filterType?.selectedTerms.length > 0,
                  selectedTerms: filterType?.selectedTerms ?? [],
                });
              }, availableFilters)}
              handleClearFilters={handleClearAllFilters}
              handleFacetClick={handleFilterClick}
              handleFacetSelection={handleFilterSelected}
              loading={isBlank(chunks)}
              selectedFacet={selectedFilter}
              selectedFacets={selectedFilters}
              totalSelectedTerms={pipe(
                map(prop("selectedTerms")),
                map(length),
                reduce(add, 0),
              )(selectedFilters)}
            />
          </Grid>
        </Grid>
        <Table stickyHeader aria-label="Item">
          <TableHead>
            <TableRow>
              <TableCell
                key="chunkId"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Chunk Id
              </TableCell>

              <TableCell
                key="mediaId"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Media id
              </TableCell>

              <TableCell
                key="title"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Title
              </TableCell>

              <TableCell
                key="isbn"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                ISBN
              </TableCell>

              <TableCell
                key="type"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Type
              </TableCell>

              <TableCell
                key="status"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {map(
              chunk => (
                <TableRow hover tabIndex={-1} key={chunk.chunkId}>
                  <TableCell
                    key={"chunkId:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    <Tooltip title="View Chunk Details">
                      <Link
                        className={classes.link}
                        to={`${url}/${chunk.chunkId}`}
                      >
                        {chunk.chunkId}
                      </Link>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    key={"media-id:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {chunk.mediaId}
                  </TableCell>

                  <TableCell
                    key={"title:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {chunk.title}
                  </TableCell>

                  <TableCell
                    key={"isbn:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {chunk.isbn}
                  </TableCell>

                  <TableCell
                    key={"type:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {chunk.mediaType}
                  </TableCell>

                  <TableCell
                    key={"status:" + chunk.chunkId}
                    align="left"
                    style={{ minWidth: 75, textTransform: "capitalize" }}
                  >
                    {chunk.status}
                  </TableCell>
                </TableRow>
              ),
              slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
                filteredChunks,
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        classes={{
          root: classes.pagination,
        }}
        colSpan={4}
        component="div"
        count={filteredChunks.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Paper>
  );
};

export default JobChunksTable;
